import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9pe2kpn2trhb35gvdn0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };
  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 43423漫画网',
    description: '43423漫画网是一个免费高清漫画在线阅读平台，提供下拉式漫画阅读体验。',
    url: 'https://43423mh.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '43423漫画网',
      url: 'https://43423mh.com',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '43423漫画网是否需要注册才能阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要！43423漫画网所有漫画均可免费在线阅读，无需注册或登录，打开网站即可畅读。',
        },
      },
      {
        '@type': 'Question',
        name: '43423漫画网支持哪些阅读方式？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '43423漫画网支持下拉式阅读模式，您只需向下滚动即可连续浏览漫画内容，提供流畅的高清阅读体验。同时支持PC端和移动端访问。',
        },
      },
      {
        '@type': 'Question',
        name: '43423漫画网的漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '43423漫画网上的连载漫画通常每周更新一话，具体更新频率因作品而异。您可以在每部漫画的详情页查看最新更新日期。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系43423漫画网？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过邮箱 support@43423mh.com 联系我们，或填写网站上的联系表单。我们的客服工作时间为工作日 9:00-18:00。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10 md:pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="mt-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              关于 43423漫画网
            </h1>
            <p className="text-sm md:text-base text-foreground-500 text-center mt-4 max-w-2xl mx-auto leading-relaxed">
              43423漫画网致力于为广大漫画爱好者提供<strong>免费高清的漫画在线阅读</strong>服务。我们采用<strong>下拉式漫画</strong>阅读模式，让您可以流畅地浏览每一页漫画内容。无论是<strong>家教老师漫画</strong>等热门恋爱作品，还是热血战斗、玄幻修仙等各类型漫画，这里应有尽有。
            </p>
          </section>

          {/* Stats */}
          <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '2000+', label: '漫画作品' },
              { value: '10万+', label: '章节更新' },
              { value: '50万+', label: '每日读者' },
              { value: '8大', label: '漫画分类' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-6 rounded-lg bg-background-100/80">
                <p className="text-2xl md:text-3xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-heading)' }}>
                  {stat.value}
                </p>
                <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </section>

          {/* Brand Story */}
          <section className="mt-12 md:mt-16">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              品牌故事
            </h2>
            <div className="mt-6 max-w-3xl mx-auto space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                43423漫画网诞生于2020年，由一群热爱漫画的年轻人创建。我们深知漫迷们在寻找高质量免费漫画阅读平台时遇到的困难——广告过多、画质模糊、更新缓慢等问题一直困扰着大家。
              </p>
              <p>
                因此，我们决心打造一个真正以用户体验为核心的<strong>漫画在线阅读平台</strong>。经过多年的努力和积累，43423漫画网已经成为拥有超过2000部漫画作品的大型漫画网站，涵盖<strong>热血、恋爱、玄幻、搞笑、悬疑、校园、都市、科幻</strong>等八大分类。
              </p>
              <p>
                我们始终坚持以用户为中心，不断优化阅读体验。独特的<strong>下拉式漫画</strong>阅读功能让浏览漫画像刷社交媒体一样自然流畅，无需繁琐的翻页操作。所有漫画均提供高清画质，确保每一格画面都清晰可见。
              </p>
            </div>
          </section>

          {/* Reputation */}
          <section className="mt-12 md:mt-16">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              用户口碑
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: '漫画迷小王', text: '43423漫画网是我用过最好用的漫画网站！下拉式阅读真的太方便了，而且画质超清晰，追漫体验满分。', rating: 5 },
                { name: '追更达人', text: '更新速度很快，基本上热门漫画都能第一时间看到最新话。免费还不用注册，良心网站！', rating: 5 },
                { name: '二次元少女', text: '分类很齐全，从热血到恋爱什么类型都有。家教老师漫画我追了好久，每周都期待更新！', rating: 5 },
                { name: '上班族小李', text: '通勤路上就用43423看漫画，下拉式操作特别适合单手使用，地铁上也能轻松追漫。', rating: 4 },
                { name: '学生党小陈', text: '不用花钱就能看这么多漫画真的太棒了。推荐给了好多同学，大家都在用！', rating: 5 },
                { name: '资深漫迷', text: '画质在免费漫画网站里绝对是顶尖的，而且没有烦人的弹窗广告，阅读体验很纯粹。', rating: 5 },
              ].map((review, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <span key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className={`ri-star-fill text-xs ${j < review.rating ? 'text-accent-500' : 'text-foreground-200'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                  <p className="text-xs text-foreground-400 mt-3">—— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mt-12 md:mt-16">
            <h2 className="text-lg md:text-xl font-bold text-foreground-900 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="mt-6 max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-lg bg-background-100/80">
                <h4 className="text-sm font-semibold text-foreground-800 mb-3">联系方式</h4>
                <ul className="space-y-3 text-sm text-foreground-600">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-mail-line"></i>
                    </span>
                    support@43423mh.com
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-customer-service-line"></i>
                    </span>
                    客服时间：工作日 9:00 - 18:00
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-question-answer-line"></i>
                    </span>
                    商务合作：business@43423mh.com
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-lg bg-background-50 border border-background-200/60">
                <h4 className="text-sm font-semibold text-foreground-800 mb-3">快速留言</h4>
                <form className="space-y-3" data-readdy-form="" onSubmit={handleSubmit}>
                  <div className="form-honeypot">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="您的昵称"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="您的邮箱"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                  <textarea
                    name="message"
                    placeholder="请输入您的留言..."
                    rows={3}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                  ></textarea>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">留言已成功发送，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500">{formError}</p>
                  )}
                  <button
                    type="submit"
                    className="w-full px-5 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}